/**
 * Canonical frontend ClassificationResult type.
 *
 * Mirrors the backend UnifiedAgentResult (snake_case) to serve as the
 * single transport-type seam between Convex actions and frontend consumers.
 *
 * Design rules:
 * - snake_case matches the Convex action return contract.
 * - camelCase DTOs are allowed only at presentation boundaries
 *   (PDF export, explainability tree rendering) — never here.
 * - classificationData must remain optional because user messages
 *   do not carry classification data.
 */

import { normalizeConfidenceScore } from "../utils/confidence";

// ─────────────────────────────────────────────────────────────
// Sub-types
// ─────────────────────────────────────────────────────────────

export type ClassificationStatus =
  | "FINAL"
  | "FINAL_WITH_WARNING"
  | "NEEDS_INFO"
  | "STOP"
  | "PENDING_APPROVAL";

export interface RetrievalSource {
  id: string;
  label: string;
  text: string;
  score?: number;
  origin?: string;
}

export interface DecisiveSource {
  type: "nomenclature" | "legal";
  sourceType?: string;
  sourceId?: string;
  authorityLevel?:
    | "PRIMARY_LEGISLATION"
    | "SECONDARY_LEGISLATION"
    | "OFFICIAL_GUIDANCE"
    | "CASE_LAW"
    | "INFORMAL_GUIDANCE";
  score: number;
  reason: string;
}

export interface EvidenceChainLink {
  sourceType: "nomenclature" | "legal";
  identifier: string;
  role: "AUTHORITATIVE" | "SUPPORTING" | "CORROBORATING";
  authorityLevel?:
    | "PRIMARY_LEGISLATION"
    | "SECONDARY_LEGISLATION"
    | "OFFICIAL_GUIDANCE"
    | "CASE_LAW"
    | "INFORMAL_GUIDANCE";
  contribution: number;
}

export interface RuntimeValidatorGate {
  gate_name:
    | "classify_validation_gate"
    | "rag_validation_gate"
    | "chat_validation_gate"
    | "cache_validation_gate"
    | "enterprise_validation_gate";
  validation_method: string;
  final_status: "CANDIDATE" | "FINAL" | "STOP" | "NEEDS_INFO";
  reason_codes: string[];
  blocking_reasons: string[];
  warnings: string[];
  data_quality_fallback: boolean;
  legal_chain?: RuntimeLegalChain;
  version_ids: string[];
  source_ids: string[];
  final_eligibility?: RuntimeFinalEligibility;
}

export interface RuntimeLegalChain {
  status?: "OK" | "NEEDS_INFO" | "STOP";
  reason_code?: string;
  code?: string;
  versionId?: string | null;
  sourceTable?: string;
  blockingReasons?: string[];
  warnings?: string[];
  evidence?: unknown[];
}

export interface RuntimeFinalEligibility {
  eligible?: boolean;
  blocking_reasons?: string[];
  warnings?: string[];
  missing_product_facts?: string[];
  audit_complete?: boolean;
}

export interface RuntimeValidationLayers {
  layer1_passed: boolean;
  layer2_score: number;
  layer3_passed: boolean;
  layer3_hierarchy_state?: string;
  notes?: string;
  method?: string;
  policy_version?: string;
}

export interface UniversalReasoningData {
  productFamily: string | null;
  familyConfidence: number;
  domainPackId: string | null;
  routingSource: "FAMILY" | "CHAPTER_HINT" | "KEYWORD" | "NONE";
  extractedAttributes: Array<{
    name: string;
    value: string | null;
    status: "PRESENT" | "ABSENT" | "AMBIGUOUS";
  }>;
  foundMandatory: number;
  missingMandatory: number;
  targetedClarifications: string[];
  decisionStatus: "READY" | "NEEDS_INFO" | "BLOCKED";
  decisionReason: string;
  maxAllowedDepth: string;
  depthEnforced: boolean;
  summaryBs: string;
  auditTrail: string[];
  engineVersion: string;
  blockingContradiction?: {
    code: "VALUE_CONFLICT" | "DOMAIN_CONFLICT" | "MATERIAL_FUNCTION" | "SET_SINGLE_ITEM";
    severity: "BLOCKING" | "WARNING";
    description: string;
    affectedAttributes: string[];
  };
}

// ─────────────────────────────────────────────────────────────
// Canonical transport type
// ─────────────────────────────────────────────────────────────

export interface ClassificationResult {
  hs_code: string;
  confidence: string;
  confidence_score: number;
  reasoning: string;
  gir_path: string[];
  legal_basis: string;
  status: ClassificationStatus;
  alternatives: Array<{ code: string; reason: string }>;
  mode_used: "function_calling" | "rag";
  tools_used: string[];
  processing_time_ms: number;
  from_cache: boolean;
  retrieval_sources?: RetrievalSource[];
  caseId?: string;
  decisionId?: string;
  correlationId?: string;
  decisiveSource?: DecisiveSource;
  evidenceChain?: EvidenceChainLink[];
  universalReasoning?: UniversalReasoningData;
  validatorGate?: RuntimeValidatorGate;
  validation?: RuntimeValidationLayers;
  missing_info?: string | null;
  suggested_questions?: string[];
}

// ─────────────────────────────────────────────────────────────
// Fallback factory
// ─────────────────────────────────────────────────────────────

export function createEmptyClassificationResult(): ClassificationResult {
  return {
    hs_code: "UNKNOWN",
    confidence: "N/A",
    confidence_score: 0,
    reasoning: "",
    gir_path: [],
    legal_basis: "",
    status: "NEEDS_INFO",
    alternatives: [],
    mode_used: "rag",
    tools_used: [],
    processing_time_ms: 0,
    from_cache: false,
  };
}

// ─────────────────────────────────────────────────────────────
// Normalizer
// ─────────────────────────────────────────────────────────────

function normalizeStatus(raw: unknown): ClassificationStatus {
  if (
    raw === "FINAL" ||
    raw === "FINAL_WITH_WARNING" ||
    raw === "NEEDS_INFO" ||
    raw === "STOP" ||
    raw === "PENDING_APPROVAL"
  ) {
    return raw;
  }
  return "NEEDS_INFO";
}

export function normalizeClassificationResult(raw: unknown): ClassificationResult {
  if (!raw || typeof raw !== "object") {
    return createEmptyClassificationResult();
  }

  const data = raw as Record<string, unknown>;

  const rawScore = data.confidence_score;
  const normalizedScore = normalizeConfidenceScore(rawScore);

  const hs_code =
    typeof data.hs_code === "string" && data.hs_code.length > 0
      ? data.hs_code
      : "UNKNOWN";

  return {
    hs_code,
    confidence: typeof data.confidence === "string" ? data.confidence : "N/A",
    confidence_score: normalizedScore,
    reasoning: typeof data.reasoning === "string" ? data.reasoning : "",
    gir_path: Array.isArray(data.gir_path)
      ? data.gir_path.filter((s): s is string => typeof s === "string")
      : [],
    legal_basis: typeof data.legal_basis === "string" ? data.legal_basis : "",
    status: normalizeStatus(data.status),
    alternatives: normalizeAlternatives(data.alternatives),
    mode_used:
      data.mode_used === "function_calling" || data.mode_used === "rag"
        ? data.mode_used
        : "rag",
    tools_used: Array.isArray(data.tools_used)
      ? data.tools_used.filter((t): t is string => typeof t === "string")
      : [],
    processing_time_ms:
      typeof data.processing_time_ms === "number" ? data.processing_time_ms : 0,
    from_cache: Boolean(data.from_cache),
    retrieval_sources: Array.isArray(data.retrieval_sources)
      ? data.retrieval_sources
          .filter(
            (s): s is Record<string, unknown> =>
              typeof s === "object" && s !== null,
          )
          .map((s) => ({
            id: String(s.id ?? ""),
            label: String(s.label ?? ""),
            text: String(s.text ?? ""),
            score: typeof s.score === "number" ? s.score : undefined,
            origin: typeof s.origin === "string" ? s.origin : undefined,
          }))
      : undefined,
    caseId: typeof data.caseId === "string" ? data.caseId : undefined,
    decisionId: typeof data.decisionId === "string" ? data.decisionId : undefined,
    correlationId:
      typeof data.correlationId === "string" ? data.correlationId : undefined,
    decisiveSource: isDecisiveSource(data.decisiveSource)
      ? data.decisiveSource
      : undefined,
    evidenceChain: Array.isArray(data.evidenceChain)
      ? data.evidenceChain.filter(isEvidenceChainLink)
      : undefined,
    universalReasoning: normalizeUniversalReasoningData(data.universalReasoning),
    validatorGate: isRuntimeValidatorGate(data.validatorGate)
      ? data.validatorGate
      : undefined,
    validation: normalizeRuntimeValidation(data.validation),
    missing_info:
      typeof data.missing_info === "string" ? data.missing_info : null,
    suggested_questions: Array.isArray(data.suggested_questions)
      ? data.suggested_questions.filter((q): q is string => typeof q === "string")
      : undefined,
  };
}

// ─────────────────────────────────────────────────────────────
// Type guards (used by normalizer)
// ─────────────────────────────────────────────────────────────

function isDecisiveSource(raw: unknown): raw is DecisiveSource {
  if (!raw || typeof raw !== "object") return false;
  const d = raw as Record<string, unknown>;
  return (
    (d.type === "nomenclature" || d.type === "legal") &&
    typeof d.score === "number" &&
    typeof d.reason === "string"
  );
}

function isEvidenceChainLink(raw: unknown): raw is EvidenceChainLink {
  if (!raw || typeof raw !== "object") return false;
  const e = raw as Record<string, unknown>;
  return (
    (e.sourceType === "nomenclature" || e.sourceType === "legal") &&
    typeof e.identifier === "string" &&
    (e.role === "AUTHORITATIVE" ||
      e.role === "SUPPORTING" ||
      e.role === "CORROBORATING") &&
    typeof e.contribution === "number"
  );
}

function isRuntimeValidatorGate(raw: unknown): raw is RuntimeValidatorGate {
  if (!raw || typeof raw !== "object") return false;
  const gate = raw as Record<string, unknown>;
  const gateName = gate.gate_name;
  const finalStatus = gate.final_status;
  return (
    (
      gateName === "classify_validation_gate" ||
      gateName === "rag_validation_gate" ||
      gateName === "chat_validation_gate" ||
      gateName === "cache_validation_gate" ||
      gateName === "enterprise_validation_gate"
    ) &&
    typeof gate.validation_method === "string" &&
    (finalStatus === "FINAL" || finalStatus === "STOP" || finalStatus === "NEEDS_INFO") &&
    Array.isArray(gate.reason_codes) &&
    Array.isArray(gate.blocking_reasons) &&
    Array.isArray(gate.warnings) &&
    typeof gate.data_quality_fallback === "boolean" &&
    Array.isArray(gate.version_ids) &&
    Array.isArray(gate.source_ids)
  );
}

function normalizeRuntimeValidation(raw: unknown): RuntimeValidationLayers | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const validation = raw as Record<string, unknown>;
  return {
    layer1_passed: Boolean(validation.layer1_passed),
    layer2_score:
      typeof validation.layer2_score === "number" && Number.isFinite(validation.layer2_score)
        ? validation.layer2_score
        : 0,
    layer3_passed: Boolean(validation.layer3_passed),
    layer3_hierarchy_state:
      typeof validation.layer3_hierarchy_state === "string"
        ? validation.layer3_hierarchy_state
        : undefined,
    notes: typeof validation.notes === "string" ? validation.notes : undefined,
    method: typeof validation.method === "string" ? validation.method : undefined,
    policy_version:
      typeof validation.policy_version === "string"
        ? validation.policy_version
        : undefined,
  };
}

// ─────────────────────────────────────────────────────────────
// Normalizers
// ─────────────────────────────────────────────────────────────

function normalizeAlternatives(raw: unknown): Array<{ code: string; reason: string }> {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const record = item as Record<string, unknown>;
    const code = typeof record.code === "string" ? record.code.trim() : "";
    const reason = typeof record.reason === "string" ? record.reason.trim() : "";
    if (!code || !reason) return [];
    return [{ code, reason }];
  });
}

function normalizeExtractedAttribute(
  raw: unknown,
): { name: string; value: string | null; status: "PRESENT" | "ABSENT" | "AMBIGUOUS" } | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const attr = raw as Record<string, unknown>;
  const name = typeof attr.name === "string" ? attr.name : undefined;
  if (!name) return undefined;
  const value = typeof attr.value === "string" ? attr.value : null;
  const status =
    attr.status === "PRESENT" || attr.status === "ABSENT" || attr.status === "AMBIGUOUS"
      ? attr.status
      : "ABSENT";
  return { name, value, status };
}

function normalizeBlockingContradiction(
  raw: unknown,
):
  | {
      code: "VALUE_CONFLICT" | "DOMAIN_CONFLICT" | "MATERIAL_FUNCTION" | "SET_SINGLE_ITEM";
      severity: "BLOCKING" | "WARNING";
      description: string;
      affectedAttributes: string[];
    }
  | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const bc = raw as Record<string, unknown>;
  const code = bc.code;
  if (
    code !== "VALUE_CONFLICT" &&
    code !== "DOMAIN_CONFLICT" &&
    code !== "MATERIAL_FUNCTION" &&
    code !== "SET_SINGLE_ITEM"
  )
    return undefined;
  const severity = bc.severity;
  if (severity !== "BLOCKING" && severity !== "WARNING") return undefined;
  const description = typeof bc.description === "string" && bc.description.length > 0 ? bc.description : undefined;
  if (!description) return undefined;
  const affectedAttributes = Array.isArray(bc.affectedAttributes)
    ? bc.affectedAttributes.filter((a): a is string => typeof a === "string")
    : [];
  if (affectedAttributes.length === 0) return undefined;
  return { code, severity, description, affectedAttributes };
}

function normalizeUniversalReasoningData(raw: unknown): UniversalReasoningData | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const u = raw as Record<string, unknown>;

  const routingSource = u.routingSource;
  if (
    routingSource !== "FAMILY" &&
    routingSource !== "CHAPTER_HINT" &&
    routingSource !== "KEYWORD" &&
    routingSource !== "NONE"
  )
    return undefined;

  const decisionStatus = u.decisionStatus;
  if (decisionStatus !== "READY" && decisionStatus !== "NEEDS_INFO" && decisionStatus !== "BLOCKED")
    return undefined;

  return {
    productFamily: typeof u.productFamily === "string" ? u.productFamily : null,
    familyConfidence: typeof u.familyConfidence === "number" ? u.familyConfidence : 0,
    domainPackId: typeof u.domainPackId === "string" ? u.domainPackId : null,
    routingSource,
    extractedAttributes: Array.isArray(u.extractedAttributes)
      ? u.extractedAttributes
          .map(normalizeExtractedAttribute)
          .filter((x): x is NonNullable<typeof x> => x !== undefined)
      : [],
    foundMandatory: typeof u.foundMandatory === "number" ? u.foundMandatory : 0,
    missingMandatory: typeof u.missingMandatory === "number" ? u.missingMandatory : 0,
    targetedClarifications: Array.isArray(u.targetedClarifications)
      ? u.targetedClarifications.filter((t): t is string => typeof t === "string")
      : [],
    decisionStatus,
    decisionReason: typeof u.decisionReason === "string" ? u.decisionReason : "",
    maxAllowedDepth: typeof u.maxAllowedDepth === "string" ? u.maxAllowedDepth : "",
    depthEnforced: Boolean(u.depthEnforced),
    summaryBs: typeof u.summaryBs === "string" ? u.summaryBs : "",
    auditTrail: Array.isArray(u.auditTrail)
      ? u.auditTrail.filter((t): t is string => typeof t === "string")
      : [],
    engineVersion: typeof u.engineVersion === "string" ? u.engineVersion : "unknown",
    blockingContradiction: normalizeBlockingContradiction(u.blockingContradiction),
  };
}
